(() => {
  const getSlides = (slider) =>
    Array.from(slider.children).filter((element) => element.matches('li.product__media-item'));

  const getOriginalSlides = (slider) => getSlides(slider).filter((slide) => !slide.dataset.carouselClone);

  const getGallerySetting = (slider, name, fallback = '') => {
    const component = slider.closest('slider-component');
    const gallery = slider.closest('media-gallery');
    return component?.dataset[name] || gallery?.dataset[name] || fallback;
  };

  const getMobileThumbnailMode = (slider) => getGallerySetting(slider, 'mobileThumbnails', 'hide');

  const isMobileColumnsMode = (slider) =>
    getMobileThumbnailMode(slider) === 'columns' && !isDesktopViewport();

  // The V2 track is a progressive enhancement.  On desktop only the
  // `thumbnail_slider` layout is allowed to use the cloned, draggable track;
  // stacked, two-column, and thumbnail layouts must retain Dawn's native
  // layout and button behavior.  On mobile, `mobile_thumbnails` controls the
  // enhancement independently of the desktop layout.  The columns setting
  // deliberately stays native so its two-column grid is not turned into a
  // horizontal loop.
  const isDesktopViewport = () =>
    typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 750px)').matches;

  const getDesktopGalleryLayout = (slider) =>
    getGallerySetting(slider, 'galleryLayout') || getGallerySetting(slider, 'desktopLayout', 'stacked');

  const isV2Enabled = (slider) => {
    if (!slider || isMobileColumnsMode(slider)) return false;
    return !isDesktopViewport() || getDesktopGalleryLayout(slider) === 'thumbnail_slider';
  };

  const prepareClone = (item, copy, index) => {
    const clone = item.cloneNode(true);
    const sourceMediaId = item.dataset.mediaId;
    clone.id = `${item.id}-carousel-${copy}-${index}`;
    clone.dataset.mediaId = `${sourceMediaId || item.id}-carousel-${copy}-${index}`;
    clone.dataset.carouselSourceId = sourceMediaId;
    clone.dataset.carouselClone = `${copy}-${index}`;
    clone.dataset.carouselCopy = copy;
    clone.classList.add('product-media-carousel-v2__clone');
    clone.classList.remove('scroll-trigger', 'animate--fade-in', 'scroll-trigger--offscreen');
    clone.style.opacity = '1';
    clone.style.transform = 'none';
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    clone.querySelectorAll('img').forEach((image) => {
      image.loading = 'eager';
      image.decoding = 'async';
    });
    clone.querySelectorAll('a, button, video, model-viewer, [tabindex]').forEach((element) => {
      element.setAttribute('tabindex', '-1');
    });
    return clone;
  };

  const setupLoop = (slider) => {
    if (!isV2Enabled(slider)) return;

    const component = slider.closest('slider-component');
    const currentSlides = getSlides(slider);
    const currentOriginalSlides = currentSlides.filter((slide) => !slide.dataset.carouselClone);
    const currentBefore = currentSlides.filter((slide) => slide.dataset.carouselCopy === 'before');
    const currentAfter = currentSlides.filter((slide) => slide.dataset.carouselCopy === 'after');
    const copiesMatch =
      currentOriginalSlides.length > 1 &&
      currentBefore.length === currentOriginalSlides.length &&
      currentAfter.length === currentOriginalSlides.length &&
      currentBefore.every(
        (slide, index) => slide.dataset.carouselSourceId === currentOriginalSlides[index]?.dataset.mediaId
      ) &&
      currentAfter.every(
        (slide, index) => slide.dataset.carouselSourceId === currentOriginalSlides[index]?.dataset.mediaId
      );

    if (slider.dataset.loopReady === 'true' && copiesMatch) return;

    const activeMediaId = currentOriginalSlides.find((slide) => slide.classList.contains('is-active'))?.dataset.mediaId;
    slider.querySelectorAll(':scope > li[data-carousel-clone]').forEach((clone) => clone.remove());
    const originalSlides = getOriginalSlides(slider);
    if (originalSlides.length < 2) return;
    originalSlides.forEach((slide) => {
      slide.dataset.carouselCopy = 'middle';
    });

    const beforeFragment = document.createDocumentFragment();
    const afterFragment = document.createDocumentFragment();
    originalSlides.forEach((slide, index) => {
      beforeFragment.appendChild(prepareClone(slide, 'before', index));
      afterFragment.appendChild(prepareClone(slide, 'after', index));
    });
    slider.prepend(beforeFragment);
    slider.append(afterFragment);
    slider.dataset.loopReady = 'true';

    if (component) {
      component.enableSliderLooping = true;
      component.sliderItems = component.querySelectorAll('[id^="Slide-"]');
      component.resetPages?.();
      component.prevButton?.removeAttribute('disabled');
      component.nextButton?.removeAttribute('disabled');
    }

    window.requestAnimationFrame(() => {
      const middleSlides = getSlides(slider).filter((slide) => slide.dataset.carouselCopy === 'middle');
      const activeSlide = middleSlides.find((slide) => slide.dataset.mediaId === activeMediaId);
      if (middleSlides.length) slider.scrollLeft = activeSlide?.offsetLeft ?? middleSlides[0].offsetLeft;
    });
  };

  const ensureLoop = (slider) => setupLoop(slider);

  const getSlideLeft = (slider, slide) => slide.offsetLeft;

  const getNearestSlideIndex = (slider, slides) =>
    slides.reduce(
      (nearestIndex, slide, index) =>
        Math.abs(getSlideLeft(slider, slide) - slider.scrollLeft) <
        Math.abs(getSlideLeft(slider, slides[nearestIndex]) - slider.scrollLeft)
          ? index
          : nearestIndex,
      0
    );

  const getSourceMediaId = (slide) => slide?.dataset.carouselSourceId || slide?.dataset.mediaId;

  const setupMobileDots = (slider) => {
    const component = slider.closest('slider-component');
    if (!component) return;

    let dots = component.querySelector(':scope > .product-media-carousel-v2__dots');
    // The theme's mobile_thumbnails setting is the source of truth. Keep the
    // custom dots only for the "hide thumbnails" mode; "show" uses the
    // existing thumbnail gallery and "columns" uses the two-column media
    // layout. Removing stale dots also makes theme-editor setting changes
    // apply without a full page reload.
    if (!isV2Enabled(slider) || getMobileThumbnailMode(slider) !== 'hide') {
      dots?.remove();
      return;
    }

    if (!dots) {
      dots = document.createElement('div');
      dots.className = 'product-media-carousel-v2__dots';
      dots.setAttribute('role', 'tablist');
      dots.setAttribute('aria-label', 'Product media');
      component.querySelector('.slider-buttons')?.before(dots);
    }

    const originals = getOriginalSlides(slider);
    const key = originals.map((slide) => slide.dataset.mediaId).join('|');
    if (!key) return;

    if (dots.dataset.sourceKey !== key) {
      dots.replaceChildren();
      originals.forEach((slide, index) => {
        const sourceId = slide.dataset.mediaId;
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'product-media-carousel-v2__dot';
        dot.dataset.target = sourceId;
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', `Go to product image ${index + 1}`);
        dot.addEventListener('click', () => {
          const gallery = component.closest('media-gallery');
          const thumbnail = [...(gallery?.querySelectorAll('[data-target]') || [])].find(
            (item) => item.dataset.target === sourceId
          );
          const button = thumbnail?.querySelector('button');
          if (button) {
            button.click();
            return;
          }

          const middleSlide = getSlides(slider).find(
            (item) => item.dataset.carouselCopy === 'middle' && item.dataset.mediaId === sourceId
          );
          if (middleSlide) slider.scrollTo({ left: getSlideLeft(slider, middleSlide), behavior: 'smooth' });
        });
        dots.append(dot);
      });
      dots.dataset.sourceKey = key;
    }

    const sync = (sourceId) => {
      const currentDots = component.querySelector(':scope > .product-media-carousel-v2__dots');
      if (!currentDots) return;
      const slides = getSlides(slider);
      const nearest = slides.length ? slides[getNearestSlideIndex(slider, slides)] : null;
      const activeSourceId = sourceId || getSourceMediaId(nearest);
      currentDots.querySelectorAll('button').forEach((dot) => {
        const active = dot.dataset.target === activeSourceId;
        dot.setAttribute('aria-current', String(active));
      });
    };

    component._v2DotsSync = sync;
    if (component.dataset.v2DotsEventsReady !== 'true') {
      component.dataset.v2DotsEventsReady = 'true';
      component._v2DotsSlideHandler = (event) => {
        const current = event.detail?.currentElement;
        component._v2DotsSync?.(getSourceMediaId(current));
      };
      component.addEventListener('slideChanged', component._v2DotsSlideHandler);
    }

    if (component._v2DotsSlider !== slider) {
      component._v2DotsSlider?.removeEventListener('scrollend', component._v2DotsScrollHandler);
      component._v2DotsScrollHandler = () => component._v2DotsSync?.();
      slider.addEventListener('scrollend', component._v2DotsScrollHandler);
      component._v2DotsSlider = slider;
    }

    sync();
  };

  // Move the track without allowing a page-level smooth-scroll style to animate
  // the invisible recentering step. The source media is identical on both sides,
  // so the user sees one continuous transition instead of a rollback.
  const setLoopPosition = (slider, left) => {
    const previousBehavior = slider.style.scrollBehavior;
    slider.style.scrollBehavior = 'auto';
    slider.scrollLeft = left;
    slider.style.scrollBehavior = previousBehavior;
  };

  // Keep one cancellable animation for both pointer types. Native smooth
  // scrolling can remain active for more than one gesture on some mobile
  // browsers, so a new pointer-down must cancel the old motion first.
  const cancelSlideAnimation = (slider) => {
    if (!slider._v2AnimationFrame) return;
    window.cancelAnimationFrame(slider._v2AnimationFrame);
    slider._v2AnimationFrame = 0;
  };

  // Remove every piece of the enhancement when a responsive breakpoint or a
  // theme-editor setting selects a native layout.  This is intentionally
  // reversible: the same slider can be enhanced again after a resize without
  // duplicating clones or event handlers.
  const disableV2 = (slider) => {
    if (!slider) return;

    const component = slider.closest('slider-component');
    const slides = getSlides(slider);
    const currentSlide = slides.length ? slides[getNearestSlideIndex(slider, slides)] : null;
    const currentSourceId = getSourceMediaId(currentSlide);

    cancelSlideAnimation(slider);
    slider.querySelectorAll(':scope > li[data-carousel-clone]').forEach((clone) => clone.remove());
    getSlides(slider).forEach((slide) => {
      delete slide.dataset.carouselCopy;
      delete slide.dataset.carouselSourceId;
      delete slide.dataset.carouselClone;
      slide.removeAttribute('aria-hidden');
    });

    const originals = getOriginalSlides(slider);
    const target = originals.find((slide) => slide.dataset.mediaId === currentSourceId) || originals[0];
    if (target) {
      const previousBehavior = slider.style.scrollBehavior;
      slider.style.scrollBehavior = 'auto';
      slider.scrollLeft = target.offsetLeft;
      slider.style.scrollBehavior = previousBehavior;
    }

    slider.classList.remove('is-mouse-dragging');
    delete slider.dataset.loopReady;
    delete slider.dataset.v2ColumnsMode;
    delete slider.dataset.v2ButtonMoving;
    delete slider.dataset.v2ButtonPending;
    delete slider.dataset.v2PointerActive;
    delete slider.dataset.v2TouchActive;
    delete slider.dataset.v2TouchMoving;
    delete slider.dataset.suppressClick;
    // These inline values are written only by this file. Clearing them lets
    // Dawn's native CSS restore the configured stacked/grid/thumbnail layout.
    slider.style.touchAction = '';
    slider.style.scrollBehavior = '';
    slider.style.scrollSnapType = '';

    if (component) {
      component.enableSliderLooping = false;
      component.sliderItems = component.querySelectorAll('[id^="Slide-"]');
      component.resetPages?.();
      component.querySelector(':scope > .product-media-carousel-v2__dots')?.remove();
    }
  };

  const animateSlideTo = (slider, left, duration = 240, onComplete) => {
    cancelSlideAnimation(slider);
    const start = slider.scrollLeft;
    const distance = left - start;
    if (Math.abs(distance) < 0.5) {
      setLoopPosition(slider, left);
      onComplete?.();
      return;
    }

    const startedAt = performance.now();
    const step = (now) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      slider.style.scrollBehavior = 'auto';
      slider.scrollLeft = start + distance * eased;
      if (progress < 1) {
        slider._v2AnimationFrame = window.requestAnimationFrame(step);
        return;
      }

      slider._v2AnimationFrame = 0;
      setLoopPosition(slider, left);
      onComplete?.();
    };

    slider._v2AnimationFrame = window.requestAnimationFrame(step);
  };

  const normalizeLoopPosition = (slider) => {
    if (!isV2Enabled(slider)) return;

    if (
      slider.classList.contains('is-mouse-dragging') ||
      slider.dataset.v2PointerActive === 'true' ||
      slider.dataset.v2TouchMoving === 'true'
    )
      return;

    const slides = getSlides(slider);
    const middleSlides = slides.filter((slide) => slide.dataset.carouselCopy === 'middle');
    if (middleSlides.length < 2) return;

    const slideOffset = middleSlides[1].offsetLeft - middleSlides[0].offsetLeft;
    if (slideOffset <= 0) return;

    const copyOffset = slideOffset * middleSlides.length;
    const edgeTolerance = slideOffset * 0.35;
    let nextScrollLeft = slider.scrollLeft;
    const middleStart = middleSlides[0].offsetLeft;
    const middleEnd = middleSlides[middleSlides.length - 1].offsetLeft;

    while (nextScrollLeft < middleStart - edgeTolerance) nextScrollLeft += copyOffset;
    while (nextScrollLeft > middleEnd + edgeTolerance) nextScrollLeft -= copyOffset;

    if (nextScrollLeft !== slider.scrollLeft) setLoopPosition(slider, nextScrollLeft);
  };

  const scrollToNearestSlide = (slider, behavior = 'smooth') => {
    if (!isV2Enabled(slider)) return;

    const slides = getSlides(slider);
    if (!slides.length) return;

    const nearest = slides.reduce((best, slide) => {
      const bestDistance = Math.abs(getSlideLeft(slider, best) - slider.scrollLeft);
      const slideDistance = Math.abs(getSlideLeft(slider, slide) - slider.scrollLeft);
      return slideDistance < bestDistance ? slide : best;
    }, slides[0]);
    const targetLeft = getSlideLeft(slider, nearest);
    if (behavior === 'auto') {
      setLoopPosition(slider, targetLeft);
      return;
    }
    animateSlideTo(slider, targetLeft);
  };

  // Arrow presses can arrive before the previous smooth scroll has settled.
  // Keep them in a small logical queue instead of recalculating from a
  // fractional scrollLeft, which can otherwise make the track visibly move
  // backwards at the clone boundary.
  const moveToAdjacentSlide = (slider, direction) => {
    if (!isV2Enabled(slider)) return;
    if (slider.dataset.v2ButtonMoving === 'true') return;

    const slides = getSlides(slider);
    const middleSlides = slides.filter((slide) => slide.dataset.carouselCopy === 'middle');
    if (middleSlides.length < 2) return;

    let currentIndex = getNearestSlideIndex(slider, slides);
    if (direction > 0 && currentIndex >= slides.length - 1) {
      setLoopPosition(slider, getSlideLeft(slider, middleSlides[middleSlides.length - 1]));
      currentIndex = getNearestSlideIndex(slider, slides);
    } else if (direction < 0 && currentIndex <= 0) {
      setLoopPosition(slider, getSlideLeft(slider, middleSlides[0]));
      currentIndex = getNearestSlideIndex(slider, slides);
    }

    const targetIndex = currentIndex + direction;
    if (targetIndex < 0 || targetIndex >= slides.length) return;

    slider.dataset.v2ButtonMoving = 'true';
    animateSlideTo(slider, getSlideLeft(slider, slides[targetIndex]), 240, () => {
      slider.dataset.v2ButtonMoving = 'false';
      normalizeLoopPosition(slider);
      window.requestAnimationFrame(() => runQueuedButtonMove(slider));
    });
  };

  const runQueuedButtonMove = (slider) => {
    if (slider.dataset.v2ButtonMoving === 'true') return;

    const pending = Number.parseInt(slider.dataset.v2ButtonPending || '0', 10);
    if (!pending) return;

    const direction = pending > 0 ? 1 : -1;
    slider.dataset.v2ButtonPending = String(pending - direction);
    moveToAdjacentSlide(slider, direction);
  };

  const addLoopButtonHandler = (slider) => {
    const component = slider.closest('slider-component');
    if (!component || component.dataset.loopButtonReady === 'true') return;

    component.dataset.loopButtonReady = 'true';
    component.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        const button = target instanceof Element ? target.closest('.slider-buttons button[name]') : null;
        if (!button) return;

        const currentSlider = component.querySelector('.product-media-carousel-v2__list');
        if (!currentSlider || !isV2Enabled(currentSlider)) return;
        ensureLoop(currentSlider);
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const direction = button.name === 'next' ? 1 : -1;
        const pending = Number.parseInt(currentSlider.dataset.v2ButtonPending || '0', 10);
        currentSlider.dataset.v2ButtonPending = String(pending + direction);
        runQueuedButtonMove(currentSlider);
      },
      true
    );
  };

  const initMouseDrag = (root = document) => {
    root.querySelectorAll('.product-media-carousel-v2__list').forEach((slider) => {
      // Keep every native desktop layout (and mobile two-column mode) out of
      // the V2 track.  This branch also cleans up clones/styles when a
      // responsive resize switches away from the enhanced mode.
      if (!isV2Enabled(slider)) {
        disableV2(slider);
        return;
      }

      delete slider.dataset.v2ColumnsMode;
      ensureLoop(slider);
      addLoopButtonHandler(slider);
      setupMobileDots(slider);
      if (slider.dataset.mouseDragReady === 'true') return;
      slider.dataset.mouseDragReady = 'true';
      // Horizontal touch movement is handled below so each gesture can land
      // on exactly one slide. Leave vertical panning and pinch zoom to the
      // browser for normal page scrolling and zooming.
      slider.style.touchAction = 'pan-y pinch-zoom';
      slider.style.scrollBehavior = 'auto';

      let dragging = false;
      let moved = false;
      let horizontalGesture = false;
      let pointerType = '';
      let startX = 0;
      let startY = 0;
      let startScrollLeft = 0;
      let startSlideIndex = 0;
      // Touch events are handled separately from PointerEvents. Some mobile
      // browsers dispatch a pointercancel/lostpointercapture as soon as a
      // horizontal gesture starts, which used to swallow every few swipes.
      let touchTracking = false;
      let touchHorizontal = false;
      let touchMoved = false;
      let touchStartX = 0;
      let touchStartY = 0;
      let touchStartScrollLeft = 0;
      let touchStartSlideIndex = 0;
      let normalizeTimer;
      let normalizeFrame;

      const settleLoop = () => {
        if (!isV2Enabled(slider)) return;
        window.clearTimeout(normalizeTimer);
        if (slider.dataset.v2TouchMoving === 'true') delete slider.dataset.v2TouchMoving;
        normalizeLoopPosition(slider);
        if (slider.dataset.v2ButtonMoving === 'true') {
          slider.dataset.v2ButtonMoving = 'false';
          window.requestAnimationFrame(() => runQueuedButtonMove(slider));
        }
      };

      const scheduleNormalize = () => {
        if (!isV2Enabled(slider)) return;
        window.clearTimeout(normalizeTimer);
        if (!normalizeFrame) {
          normalizeFrame = window.requestAnimationFrame(() => {
            normalizeFrame = 0;
            // Recenter while momentum is moving, before the track can hit its
            // hard end. Mouse dragging is handled after release by scrollend.
            if (
              slider.dataset.v2ButtonMoving !== 'true' &&
              slider.dataset.v2TouchMoving !== 'true' &&
              !slider.classList.contains('is-mouse-dragging') &&
              slider.dataset.v2PointerActive !== 'true'
            ) {
              normalizeLoopPosition(slider);
            }
          });
        }
        if (!('onscrollend' in slider)) normalizeTimer = window.setTimeout(settleLoop, 350);
      };

      slider.addEventListener('scroll', scheduleNormalize, { passive: true });
      slider.addEventListener('scrollend', settleLoop, { passive: true });
      slider.addEventListener('pointerdown', (event) => {
        // Touch and pen input use the native touch handlers below. Keeping
        // those out of this PointerEvent path prevents duplicate state and
        // lostpointercapture from ending a valid swipe early.
        if (!isV2Enabled(slider) || event.pointerType !== 'mouse' || event.button !== 0) return;
        cancelSlideAnimation(slider);
        normalizeLoopPosition(slider);
        const currentSlides = getSlides(slider);
        if (currentSlides.length) {
          const nearest = currentSlides[getNearestSlideIndex(slider, currentSlides)];
          if (nearest) setLoopPosition(slider, getSlideLeft(slider, nearest));
        }
        slider.dataset.v2PointerActive = 'true';

        dragging = true;
        moved = false;
        horizontalGesture = true;
        pointerType = 'mouse';
        startX = event.clientX;
        startY = event.clientY;
        startScrollLeft = slider.scrollLeft;
        startSlideIndex = getNearestSlideIndex(slider, getSlides(slider));
        if (pointerType === 'mouse') {
          slider.classList.add('is-mouse-dragging');
          slider.setPointerCapture?.(event.pointerId);
        }
      });

      slider.addEventListener('pointermove', (event) => {
        if (!isV2Enabled(slider) || !dragging) return;
        const delta = event.clientX - startX;
        const verticalDelta = event.clientY - startY;

        if (!moved) {
          if (Math.abs(delta) > 4) moved = true;
          if (!moved) return;
        }

        event.preventDefault();
        if (pointerType === 'mouse') {
          slider.scrollLeft = startScrollLeft - delta;
          return;
        }

        const slides = getSlides(slider);
        const slideOffset =
          slides.length > 1 ? getSlideLeft(slider, slides[1]) - getSlideLeft(slider, slides[0]) : 0;
        const maxDrag = slideOffset > 0 ? slideOffset * 0.92 : slider.clientWidth * 0.92;
        const boundedDelta = Math.max(-maxDrag, Math.min(maxDrag, delta));
        slider.scrollLeft = startScrollLeft - boundedDelta;
      });

      const stopDragging = (event) => {
        if (!dragging) return;
        if (!isV2Enabled(slider)) {
          dragging = false;
          horizontalGesture = false;
          pointerType = '';
          slider.classList.remove('is-mouse-dragging');
          if (event?.pointerId !== undefined && slider.hasPointerCapture?.(event.pointerId)) {
            slider.releasePointerCapture(event.pointerId);
          }
          return;
        }
        const wasTouchGesture = pointerType !== 'mouse' && horizontalGesture;
        dragging = false;
        slider.classList.remove('is-mouse-dragging');
        if (wasTouchGesture) slider.style.scrollSnapType = '';
        if (event?.pointerId !== undefined && slider.hasPointerCapture?.(event.pointerId)) {
          slider.releasePointerCapture(event.pointerId);
        }
        if (wasTouchGesture) {
          slider.dataset.suppressClick = 'true';
          window.setTimeout(() => delete slider.dataset.suppressClick, 100);

          const slides = getSlides(slider);
          const startSlide = slides[startSlideIndex];
          const slideOffset =
            slides.length > 1 ? getSlideLeft(slider, slides[1]) - getSlideLeft(slider, slides[0]) : 0;
          const travel = startX - (event?.clientX ?? startX);
          const threshold = Math.max(24, (slideOffset || slider.clientWidth) * 0.14);
          let direction = 0;
          if (Math.abs(travel) >= threshold) direction = travel > 0 ? 1 : -1;

          const targetIndex = Math.max(0, Math.min(slides.length - 1, startSlideIndex + direction));
          const targetSlide = slides[targetIndex] || startSlide;
          if (targetSlide) {
            // Touch gestures must settle before the next finger-down. A long
            // smooth-scroll animation can swallow the next gesture on mobile,
            // making the gallery appear to stop after a few images. The drag
            // itself follows the finger; release only needs to snap to the
            // adjacent slide immediately.
            slider.dataset.v2TouchMoving = 'true';
            setLoopPosition(slider, getSlideLeft(slider, targetSlide));
            delete slider.dataset.v2TouchMoving;
            window.requestAnimationFrame(() => {
              normalizeLoopPosition(slider);
            });
          }
        } else if (moved) {
          slider.dataset.suppressClick = 'true';
          window.setTimeout(() => delete slider.dataset.suppressClick, 100);

          // Use the same one-slide threshold for mouse drags as for touch.
          // Nearest-position snapping can send a deliberate half-slide drag
          // back to its starting image, which feels like a failed drag.
          const slides = getSlides(slider);
          const startSlide = slides[startSlideIndex];
          const slideOffset =
            slides.length > 1 ? getSlideLeft(slider, slides[1]) - getSlideLeft(slider, slides[0]) : 0;
          const travel = startX - (event?.clientX ?? startX);
          const threshold = Math.max(24, (slideOffset || slider.clientWidth) * 0.14);
          const direction = Math.abs(travel) >= threshold ? (travel > 0 ? 1 : -1) : 0;
          const targetIndex = Math.max(0, Math.min(slides.length - 1, startSlideIndex + direction));
          const targetSlide = slides[targetIndex] || startSlide;
          if (targetSlide) {
            animateSlideTo(slider, getSlideLeft(slider, targetSlide), 240, () => normalizeLoopPosition(slider));
          }
        }
        horizontalGesture = false;
        pointerType = '';
      };

      slider.addEventListener('pointerup', stopDragging);
      slider.addEventListener('pointercancel', stopDragging);
      slider.addEventListener('lostpointercapture', stopDragging);
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((eventName) => {
        slider.addEventListener(eventName, () => delete slider.dataset.v2PointerActive);
      });
      slider.addEventListener('dragstart', (event) => {
        if (!isV2Enabled(slider)) return;
        if (event.target instanceof Element && event.target.closest('a, img, video')) event.preventDefault();
      });
      slider.addEventListener(
        'click',
        (event) => {
          if (!isV2Enabled(slider) || slider.dataset.suppressClick !== 'true') return;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );

      // Embla/Swiper-style native touch handling. We wait for the gesture's
      // primary axis before calling preventDefault: vertical drags continue
      // scrolling the page, while horizontal drags are owned by this track
      // and are bounded to one slide per finger gesture.
      const clearTouchTracking = () => {
        touchTracking = false;
        touchHorizontal = false;
        touchMoved = false;
        delete slider.dataset.v2TouchActive;
        delete slider.dataset.v2TouchMoving;
        slider.style.scrollSnapType = '';
      };

      slider.addEventListener(
        'touchstart',
        (event) => {
          if (!isV2Enabled(slider)) {
            clearTouchTracking();
            return;
          }
          if (event.touches.length !== 1) {
            clearTouchTracking();
            return;
          }

          cancelSlideAnimation(slider);
          normalizeLoopPosition(slider);
          const currentSlides = getSlides(slider);
          if (!currentSlides.length) return;
          const nearestIndex = getNearestSlideIndex(slider, currentSlides);
          const nearest = currentSlides[nearestIndex];
          if (nearest) setLoopPosition(slider, getSlideLeft(slider, nearest));

          const touch = event.touches[0];
          touchTracking = true;
          touchHorizontal = false;
          touchMoved = false;
          touchStartX = touch.clientX;
          touchStartY = touch.clientY;
          touchStartScrollLeft = slider.scrollLeft;
          touchStartSlideIndex = nearestIndex;
          slider.dataset.v2TouchActive = 'true';
        },
        { passive: true }
      );

      slider.addEventListener(
        'touchmove',
        (event) => {
          if (!isV2Enabled(slider)) {
            clearTouchTracking();
            return;
          }
          if (!touchTracking || event.touches.length !== 1) return;
          const touch = event.touches[0];
          const deltaX = touch.clientX - touchStartX;
          const deltaY = touch.clientY - touchStartY;

          if (!touchHorizontal) {
            if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) <= 6) return;
            if (Math.abs(deltaY) > Math.abs(deltaX)) {
              clearTouchTracking();
              return;
            }
            touchHorizontal = true;
            slider.dataset.v2TouchMoving = 'true';
            slider.style.scrollSnapType = 'none';
          }

          event.preventDefault();
          touchMoved = true;
          const slides = getSlides(slider);
          const slideOffset =
            slides.length > 1 ? getSlideLeft(slider, slides[1]) - getSlideLeft(slider, slides[0]) : 0;
          const maxDrag = slideOffset > 0 ? slideOffset * 0.92 : slider.clientWidth * 0.92;
          const boundedDelta = Math.max(-maxDrag, Math.min(maxDrag, deltaX));
          slider.scrollLeft = touchStartScrollLeft - boundedDelta;
        },
        { passive: false }
      );

      const finishTouch = (event) => {
        if (!isV2Enabled(slider)) {
          clearTouchTracking();
          return;
        }
        if (!touchTracking) return;
        const wasHorizontal = touchHorizontal && touchMoved;
        const endTouch = event?.changedTouches?.[0];
        const endX = endTouch?.clientX ?? touchStartX;
        const travel = touchStartX - endX;
        const slides = getSlides(slider);

        if (wasHorizontal && slides.length) {
          slider.dataset.suppressClick = 'true';
          window.setTimeout(() => delete slider.dataset.suppressClick, 250);
          const slideOffset =
            slides.length > 1 ? getSlideLeft(slider, slides[1]) - getSlideLeft(slider, slides[0]) : 0;
          const threshold = Math.max(24, (slideOffset || slider.clientWidth) * 0.14);
          const direction = Math.abs(travel) >= threshold ? (travel > 0 ? 1 : -1) : 0;
          const targetIndex = Math.max(0, Math.min(slides.length - 1, touchStartSlideIndex + direction));
          const targetSlide = slides[targetIndex];
          if (targetSlide) {
            // The finger already moved the track. Snap synchronously so the
            // next touch can start immediately without waiting on momentum.
            setLoopPosition(slider, getSlideLeft(slider, targetSlide));
          }
        }

        clearTouchTracking();
        if (wasHorizontal) window.requestAnimationFrame(() => normalizeLoopPosition(slider));
      };

      slider.addEventListener('touchend', finishTouch, { passive: true });
      slider.addEventListener('touchcancel', finishTouch, { passive: true });

      // Desktop reference galleries use the left and right halves of the
      // media area as navigation targets. Keep controls and links untouched;
      // a genuine drag is already filtered by the capture handler above.
      slider.addEventListener('click', (event) => {
        if (!isV2Enabled(slider) || !isDesktopViewport()) return;
        if (event.detail === 0 || slider.dataset.suppressClick === 'true') return;
        const target = event.target;
        if (target instanceof Element && target.closest('button, a, video, model-viewer')) return;
        const bounds = slider.getBoundingClientRect();
        moveToAdjacentSlide(slider, event.clientX < bounds.left + bounds.width / 2 ? -1 : 1);
      });
    });
  };

  const init = (root = document) => {
    initMouseDrag(root);
    root.querySelectorAll('.product-media-carousel-v2').forEach((component) => {
      if (component.dataset.loopObserverReady === 'true') return;
      component.dataset.loopObserverReady = 'true';
      const observer = new MutationObserver(() => {
        const slider = component.querySelector('.product-media-carousel-v2__list');
        if (slider) window.requestAnimationFrame(() => initMouseDrag(component));
      });
      observer.observe(component, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-gallery-layout', 'data-mobile-thumbnails'],
      });
    });
  };

  // Layout settings and the breakpoint can change while the product page is
  // open (window resize, orientation change, or theme-editor preview).  Run
  // the same reversible gate after the resize settles so a native layout never
  // keeps stale clones or capture handlers from the previous mode.
  let responsiveRefreshTimer;
  const refreshResponsiveMode = () => {
    window.clearTimeout(responsiveRefreshTimer);
    responsiveRefreshTimer = window.setTimeout(() => init(), 80);
  };
  window.addEventListener('resize', refreshResponsiveMode, { passive: true });
  window.addEventListener('orientationchange', refreshResponsiveMode, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
